/* Query and mutation hooks for Events — STORY-011.
 *
 * Server state lives in TanStack Query and nowhere else. There is no store
 * holding "the current Event" alongside this: a second copy is a second thing
 * to invalidate, and the one that gets forgotten is always the one on screen.
 */

import {
  type CreateEventRequest,
  eventHistoryResponseSchema,
  eventResponseSchema,
  eventSchema,
  eventsResponseSchema,
  type ListEventsQuery,
  type TransitionEventRequest,
  type UpdateEventRequest,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../../../shared/api/client.js';
import { toSearchParams } from '../filters.js';

/**
 * The key every read of the list shares — STORY-040.
 *
 * The query is part of it, so two filter sets are two cached pages rather than
 * one that flickers between them. Every write still invalidates the PREFIX, so
 * a rename or a move refreshes whichever page is on screen without any caller
 * having to know what it was filtered by.
 */
export const eventsKey = (organisationId: string, query?: ListEventsQuery) =>
  query === undefined
    ? (['organisations', organisationId, 'events'] as const)
    : (['organisations', organisationId, 'events', query] as const);

export const eventKey = (organisationId: string, eventId: string) =>
  ['organisations', organisationId, 'events', 'one', eventId] as const;

/**
 * One Event, by its id — added 2026-09-05.
 *
 * The detail screen used to take its Event out of the LIST response, which
 * quietly made two ordinary things unreachable: an Event whose status the list
 * hides by default, and any Event past the first page of twenty-five. So
 * cancelling one from its own page ended on "that event is not here", and a
 * bookmark to a later Event did the same.
 *
 * A record is addressed by its id, never found by filtering a page of records.
 */
export function useEvent(organisationId: string, eventId: string) {
  return useQuery({
    queryKey: eventKey(organisationId, eventId),
    queryFn: () =>
      request(`/organisations/${organisationId}/events/${eventId}`, {
        schema: eventResponseSchema,
      }),
    enabled: Boolean(organisationId) && Boolean(eventId),
    retry: false,
  });
}

export function useEvents(organisationId: string, query: ListEventsQuery = {}) {
  /* Somebody unticked every status. There is nothing to ask the server — no
   * status matches no Event — so the screen answers it without a round trip
   * and the query never runs. */
  const wantsNothing = query.status?.length === 0;

  return useQuery({
    queryKey: eventsKey(organisationId, query),
    queryFn: () =>
      request(`/organisations/${organisationId}/events${toSearchParams(query)}`, {
        schema: eventsResponseSchema,
      }),
    enabled: !wantsNothing,
    /* The previous page stays on screen while the next one loads, so paging and
     * filtering do not blank the list and bounce the scroll position. */
    placeholderData: (previous) => previous,
    retry: false,
  });
}

export function useCreateEvent(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateEventRequest) =>
      request(`/organisations/${organisationId}/events`, {
        method: 'POST',
        schema: eventSchema,
        body: values,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: eventsKey(organisationId) });
    },
  });
}

/**
 * One field at a time.
 *
 * The body carries only what changed, which is the point rather than an
 * optimisation: an absent key leaves a field alone and `null` clears it, so
 * saving a name cannot wipe a budget set elsewhere on the screen.
 */
export function useUpdateEvent(organisationId: string, eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: UpdateEventRequest) =>
      request(`/organisations/${organisationId}/events/${eventId}`, {
        method: 'PATCH',
        schema: eventSchema,
        body: values,
      }),
    onSuccess: async () => {
      /* Invalidated rather than seeded: several fields on this screen save
       * independently, and two in flight at once would otherwise race to write
       * the cache with each one's idea of the whole Event. */
      await queryClient.invalidateQueries({ queryKey: eventsKey(organisationId) });
    },
  });
}

/**
 * Moving an Event — STORY-012.
 *
 * `from` is sent with every move, so two people on two phones resolve to one
 * winner and the loser is told rather than silently overwriting. The caller is
 * looking at the Event, so it already knows.
 */
export function useTransitionEvent(organisationId: string, eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: TransitionEventRequest) =>
      request(`/organisations/${organisationId}/events/${eventId}/status`, {
        method: 'POST',
        schema: eventSchema,
        body: values,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: eventsKey(organisationId) });
      /* The trail gained a row. Without this the history sits one move behind
       * the badge above it, which reads as a bug in the trail rather than in
       * the cache. */
      await queryClient.invalidateQueries({ queryKey: historyKey(organisationId, eventId) });
    },
    onError: async (error) => {
      /* A conflict means the Event moved underneath this screen, so the copy
       * says what it is now — but nothing else on the screen knew that, and
       * that made the refusal a dead end. The cached Event kept its old
       * status, pressing Save resent the same stale `from`, and it conflicted
       * again forever. `refetchOnWindowFocus` is off app-wide, so there was no
       * other way back short of a page reload.
       *
       * Only on a conflict: an entitlement refusal or a network failure
       * changed nothing on the server, and refetching would be a request that
       * cannot tell anybody anything new. */
      if (error instanceof ApiError && error.code === 'conflict') {
        await queryClient.invalidateQueries({ queryKey: eventsKey(organisationId) });
        await queryClient.invalidateQueries({ queryKey: historyKey(organisationId, eventId) });
      }
    },
  });
}

export const historyKey = (organisationId: string, eventId: string) =>
  ['organisations', organisationId, 'events', eventId, 'history'] as const;

export function useEventHistory(organisationId: string, eventId: string) {
  return useQuery({
    queryKey: historyKey(organisationId, eventId),
    queryFn: () =>
      request(`/organisations/${organisationId}/events/${eventId}/history`, {
        schema: eventHistoryResponseSchema,
      }),
    retry: false,
  });
}
